import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence, useAnimationControls } from 'framer-motion';
import { Eye, EyeOff } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { getErrorMessage } from '../utils/errors';
import { postApi } from '../services/apiFetch';
import ShoreScene from '../components/bridge/ShoreScene';
import BridgeMark from '../components/BridgeMark';

// Signing in lays the bridge: every filled field is a plank, and a successful
// sign-in sends the note across to the clinic before the app opens.

type FieldId = 'username' | 'name' | 'invite' | 'password';
type FieldErrors = Partial<Record<FieldId, string>>;

// A server error that points at fields: the last field listed shows the message,
// the others are only marked. Errors without fields use the general message box.
const ERRORS: [string, string, FieldId[]][] = [
  ['Invalid login', 'שם המשתמש או הסיסמה לא נכונים. אפשר לנסות שוב.', ['username', 'password']],
  ['username_taken', 'שם המשתמש הזה כבר תפוס. נסו שם אחר.', ['username']],
  ['invalid_invite_code', 'קוד ההזמנה לא נכון. כדאי לבדוק שהעתקתם אותו במלואו.', ['invite']],
  ['registration_closed', 'ההרשמה למטפלים סגורה כרגע.', []],
  ['invalid_username', 'שם משתמש: 3 עד 30 תווים באנגלית, ספרות, נקודה, מקף או קו תחתון.', ['username']],
  ['weak_password', 'הסיסמה צריכה לפחות 8 תווים.', ['password']],
];

// error === undefined: fine. '' : marked invalid, the explanation sits on another field.
function Field({
  id, label, value, hint, error, shakeKey = 0, children,
}: { id: string; label: string; value: string; hint?: string; error?: string; shakeKey?: number; children: React.ReactNode }) {
  const controls = useAnimationControls();
  useEffect(() => {
    if (shakeKey > 0 && error !== undefined) {
      controls.start({ x: [0, -9, 9, -6, 6, -3, 0], transition: { duration: 0.45 } });
    }
    // Only a new failed attempt should shake the field.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shakeKey]);

  return (
    <motion.div animate={controls} className="flex flex-col gap-1.5">
      {/* Floating label: rests inside the field, lifts onto the border on focus or once there is text. */}
      <div className="group relative">
        {children}
        <label
          htmlFor={id}
          className={`pointer-events-none absolute right-3 z-10 -translate-y-1/2 rounded px-1.5 transition-all duration-200 ease-out ${
            value.length > 0
              ? 'top-0 bg-background text-xs font-bold'
              : 'top-1/2 text-base group-focus-within:top-0 group-focus-within:bg-background group-focus-within:text-xs group-focus-within:font-bold'
          } ${error !== undefined ? 'text-accent' : 'text-muted-foreground group-focus-within:text-bridge'}`}
        >
          {label}
        </label>
      </div>
      {error ? (
        <p id={`${id}-error`} role="alert" className="text-xs font-medium text-accent">{error}</p>
      ) : hint && error === undefined ? (
        <p id={`${id}-hint`} className="text-xs text-muted-foreground">{hint}</p>
      ) : null}
    </motion.div>
  );
}

const inputCls =
  'w-full rounded-xl border-2 border-border bg-background px-3.5 py-3 text-base text-foreground placeholder:text-transparent focus:placeholder:text-muted-foreground/70 ' +
  'focus:border-bridge focus:outline-none focus:ring-4 focus:ring-bridge/20 transition-colors';
const inputInvalidCls = 'border-accent focus:border-accent focus:ring-accent/20';

const AuthPage: React.FC = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [shakeKey, setShakeKey] = useState(0);
  const [flightKey, setFlightKey] = useState(0);

  const hour = new Date().getHours();
  const fields = isLogin ? [username, password] : [username, fullName, inviteCode, password];
  const filled = fields.filter((v) => v.trim().length > 0).length;
  // One extra plank is laid by the sign-in itself.
  const total = fields.length + 1;
  const done = filled + (flightKey > 0 ? 1 : 0);

  const switchMode = (login: boolean) => {
    setIsLogin(login);
    setError(null);
    setFieldErrors({});
  };

  // Editing a field clears its own mark; login failures mark both fields, so clear both.
  const edited = (id: FieldId) => {
    if (Object.keys(fieldErrors).length === 0) return;
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[id];
      if (prev.username !== undefined && prev.password !== undefined && (id === 'username' || id === 'password')) {
        delete next.username;
        delete next.password;
      }
      return next;
    });
  };

  const inputProps = (id: FieldId) => ({
    'aria-invalid': fieldErrors[id] !== undefined || undefined,
  });
  const invalidCls = (id: FieldId) => (fieldErrors[id] !== undefined ? ` ${inputInvalidCls}` : '');
  const errorId = (id: FieldId) => (fieldErrors[id] ? `auth-${id}-error` : undefined);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setFieldErrors({});

    const fakeEmail = `${username.trim().toLowerCase()}@remind.com`;

    try {
      if (!isLogin) {
        // Therapist accounts are created on the server and require an invite code.
        const response = await postApi('/api/register-therapist', { username, password, fullName, inviteCode });
        const result = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(result.error || 'registration_failed');
      }
      const { error } = await supabase.auth.signInWithPassword({ email: fakeEmail, password });
      if (error) throw error;
      // Let the last plank land and the note fly before the app takes over.
      setFlightKey((k) => k + 1);
      window.setTimeout(() => window.location.reload(), 1400);
    } catch (err) {
      const message = getErrorMessage(err);
      const match = ERRORS.find(([key]) => message.includes(key));
      if (match && match[2].length > 0) {
        const [, text, fields] = match;
        setFieldErrors(Object.fromEntries(fields.map((f, i) => [f, i === fields.length - 1 ? text : ''])));
        setShakeKey((k) => k + 1);
      } else {
        setError(match?.[1] ?? (message || 'משהו השתבש בהתחברות. אפשר לנסות שוב.'));
      }
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-background dir-rtl md:grid md:grid-cols-[1.1fr_1fr]">
      {/* The world: the bridge is laid as the form fills */}
      <div className="relative md:order-2 md:h-[100dvh] md:sticky md:top-0 md:flex md:flex-col md:items-center md:justify-center md:gap-5 md:bg-muted md:p-10 overflow-hidden">
        <div className="relative w-full md:max-w-xl md:overflow-hidden md:rounded-[32px] md:shadow-[0_12px_40px_-12px_hsl(var(--primary)/0.35)]">
          <ShoreScene
            feeling={null}
            hour={hour}
            total={total}
            done={done}
            avatarId="default"
            flightKey={flightKey}
            className="block w-full aspect-[390/300] md:aspect-[390/360]"
          />
          <div className="absolute inset-x-0 top-0 px-5 pt-5 flex items-center gap-2.5">
            <BridgeMark size={36} className="rounded-xl shadow-sm" />
            <span className="text-xl font-extrabold tracking-tight text-foreground drop-shadow-[0_1px_0_rgba(255,255,255,0.6)]">ReMind</span>
          </div>
        </div>
        <p className="hidden md:block font-hand text-3xl text-muted-foreground">בין הפגישה לבית, יש גשר.</p>
      </div>

      {/* The near shore: the form */}
      <main className="relative -mt-8 md:mt-0 rounded-t-[32px] md:rounded-none bg-background px-5 pt-7 pb-10 md:flex md:items-center md:justify-center">
        <div className="w-full max-w-sm mx-auto">
          <h1 className="font-hand text-5xl leading-none text-foreground">
            {isLogin ? 'ברוכים השבים' : 'מטפל/ת חדש/ה'}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {isLogin
              ? 'הגשר בין הפגישה לבית. כל שדה שממלאים מניח עוד קרש.'
              : 'חשבון מטפל נפתח עם קוד הזמנה מצוות ReMind.'}
          </p>

          <div role="tablist" aria-label="כניסה או הרשמה" className="mt-6 grid grid-cols-2 gap-1 rounded-2xl bg-muted p-1">
            {[
              { login: true, label: 'כניסה' },
              { login: false, label: 'הרשמת מטפלים' },
            ].map((t) => (
              <button
                key={t.label}
                role="tab"
                type="button"
                aria-selected={isLogin === t.login}
                onClick={() => switchMode(t.login)}
                className={`rounded-xl py-2.5 text-sm font-bold transition-colors ${
                  isLogin === t.login ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <form onSubmit={handleAuth} className="mt-5 flex flex-col gap-4" noValidate={false}>
            <Field id="auth-username" label="שם משתמש" value={username} hint={isLogin ? 'מטופלים מקבלים שם משתמש מהמטפל/ת.' : undefined} error={fieldErrors.username} shakeKey={shakeKey}>
              <input
                id="auth-username"
                className={`${inputCls}${invalidCls('username')}`}
                type="text"
                dir="ltr"
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                placeholder="dana.k"
                value={username}
                onChange={(e) => { setUsername(e.target.value.trim()); edited('username'); }}
                {...inputProps('username')}
                aria-describedby={errorId('username') ?? (isLogin ? 'auth-username-hint' : undefined)}
                required
              />
            </Field>

            <AnimatePresence initial={false}>
              {!isLogin && (
                <motion.div
                  key="register-fields"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="-mx-2 -mt-2 flex flex-col gap-4 overflow-hidden px-2 pt-2"
                >
                  <Field id="auth-name" label="שם מלא" value={fullName} error={fieldErrors.name} shakeKey={shakeKey}>
                    <input
                      id="auth-name"
                      className={`${inputCls}${invalidCls('name')}`}
                      type="text"
                      autoComplete="name"
                      placeholder="השם שהמטופלים יראו"
                      value={fullName}
                      onChange={(e) => { setFullName(e.target.value); edited('name'); }}
                      {...inputProps('name')}
                      aria-describedby={errorId('name')}
                      required
                    />
                  </Field>
                  <Field id="auth-invite" label="קוד הזמנה" value={inviteCode} hint="הקוד שקיבלת מצוות ReMind." error={fieldErrors.invite} shakeKey={shakeKey}>
                    <input
                      id="auth-invite"
                      className={`${inputCls}${invalidCls('invite')}`}
                      type="text"
                      dir="ltr"
                      autoComplete="off"
                      {...inputProps('invite')}
                      aria-describedby={errorId('invite') ?? 'auth-invite-hint'}
                      autoCapitalize="none"
                      placeholder="RM-XXXX"
                      value={inviteCode}
                      onChange={(e) => { setInviteCode(e.target.value); edited('invite'); }}
                      required
                    />
                  </Field>
                </motion.div>
              )}
            </AnimatePresence>

            <Field id="auth-password" label="סיסמה" value={password} hint={isLogin ? undefined : 'לפחות 8 תווים.'} error={fieldErrors.password} shakeKey={shakeKey}>
              <div className="relative">
                <input
                  id="auth-password"
                  className={`${inputCls}${invalidCls('password')} pl-12`}
                  type={showPassword ? 'text' : 'password'}
                  dir="ltr"
                  autoComplete={isLogin ? 'current-password' : 'new-password'}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); edited('password'); }}
                  {...inputProps('password')}
                  aria-describedby={errorId('password') ?? (isLogin ? undefined : 'auth-password-hint')}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-pressed={showPassword}
                  aria-label={showPassword ? 'הסתר סיסמה' : 'הצג סיסמה'}
                  title={showPassword ? 'הסתר סיסמה' : 'הצג סיסמה'}
                  className={`absolute left-2 top-1/2 -translate-y-1/2 rounded-lg p-2 transition-colors ${
                    showPassword ? 'text-bridge' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {/* Open eye = the password is visible; slashed eye = hidden. */}
                  {showPassword ? <Eye size={20} aria-hidden="true" /> : <EyeOff size={20} aria-hidden="true" />}
                </button>
              </div>
            </Field>

            {error && (
              <p role="alert" className="rounded-xl border border-accent/30 bg-accent/10 px-3.5 py-2.5 text-sm font-medium text-foreground">
                {error}
              </p>
            )}

            <motion.button
              type="submit"
              disabled={loading}
              whileHover={loading ? undefined : { y: -2 }}
              whileTap={loading ? undefined : { y: 3 }}
              transition={{ type: 'spring', stiffness: 500, damping: 28 }}
              className="mt-1 w-full rounded-2xl bg-primary py-3.5 text-base font-bold text-primary-foreground shadow-[0_4px_0_hsl(var(--primary)/0.45)] transition-[box-shadow,opacity] hover:shadow-[0_6px_0_hsl(var(--primary)/0.45)] active:shadow-[0_1px_0_hsl(var(--primary)/0.45)] disabled:opacity-70"
            >
              {flightKey > 0 ? 'חוצים את הגשר...' : loading ? 'רגע...' : isLogin ? 'כניסה' : 'פתיחת חשבון מטפל'}
            </motion.button>
          </form>

          <p className="mt-8 text-center text-xs text-muted-foreground">
            <a href="/?home" className="font-bold underline-offset-4 hover:text-foreground hover:underline">
              מה זה ReMind?
            </a>
            {' · '}remind-healtech.com
          </p>
        </div>
      </main>
    </div>
  );
};

export default AuthPage;
